import * as THREE from 'three';

export type VRMenuMode = 'hidden' | 'main' | 'lobby' | 'solo' | 'briefing' | 'debrief';

interface MenuButton {
  mesh: THREE.Mesh;
  action: () => void;
  baseColor: number;
  modes: VRMenuMode[];
  id?: string;
  paintLabel: () => void;
}

export interface VRMenuActions {
  exit(): void;
  solo(): void;
  random(): void;
  create(): void;
  joinCode(code: string): void;
  drive(): void;
  gun(): void;
  seatRandom(): void;
  ready(): void;
  skip(): void;
}

// same alphabet the lobby-code generator uses (no 0/O, 1/I)
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * In-headset menu: the DOM UI doesn't exist inside WebXR, so these are real
 * meshes placed relative to the actual XR camera for modal interaction. Aim
 * with either controller's ray and pull the trigger to click. Gameplay data
 * lives in the dashboard; this panel appears only between driving sequences.
 */
export class VRMenu {
  readonly group = new THREE.Group();

  private buttons: MenuButton[] = [];
  private mode: VRMenuMode = 'hidden';
  private raycaster = new THREE.Raycaster();
  private controllers: THREE.XRTargetRaySpace[] = [];
  private lasers = new Map<THREE.XRTargetRaySpace, THREE.Line>();
  private hovered: MenuButton | null = null;
  private busy = false;
  private lastActivationAt = 0;

  private statusCanvas: HTMLCanvasElement;
  private statusCtx: CanvasRenderingContext2D;
  private statusTexture: THREE.CanvasTexture;
  private statusLines: string[] = ['GLITCH PURSUIT'];
  private logoImage: HTMLImageElement | null = null;
  private selectedId: string | null = null;

  // join-by-code entry (no keyboards in VR: cycle each character)
  private codeChars = ['A', 'A', 'A', 'A', 'A'];
  private codeCells: { ctx: CanvasRenderingContext2D; texture: THREE.CanvasTexture }[] = [];

  constructor(private actions: VRMenuActions) {
    // The group is parented to the camera, so this is relative to the real
    // headset pose rather than the car origin (which put panels under the car
    // when a runtime supplied standing-height local coordinates).
    this.group.position.set(0, -0.12, -1.35);
    this.group.scale.setScalar(0.86);
    this.group.visible = false;

    // backboard: synthwave gradient, not a flat black slab
    const boardCanvas = document.createElement('canvas');
    boardCanvas.width = 64;
    boardCanvas.height = 256;
    const bctx = boardCanvas.getContext('2d')!;
    const grad = bctx.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0, '#241454');
    grad.addColorStop(0.45, '#170f33');
    grad.addColorStop(1, '#0c0e16');
    bctx.fillStyle = grad;
    bctx.fillRect(0, 0, 64, 256);
    const boardTex = new THREE.CanvasTexture(boardCanvas);
    const board = new THREE.Mesh(
      new THREE.PlaneGeometry(1.15, 1.06),
      this.uiMaterial({ map: boardTex })
    );
    board.renderOrder = 100;
    this.group.add(board);
    const trim = new THREE.Mesh(
      new THREE.PlaneGeometry(1.19, 0.02),
      this.uiMaterial({ color: 0xff2d95 })
    );
    trim.position.set(0, 0.545, 0.001);
    trim.renderOrder = 101;
    this.group.add(trim);
    const trimBottom = new THREE.Mesh(
      new THREE.PlaneGeometry(1.19, 0.02),
      this.uiMaterial({ color: 0x2ee6d6 })
    );
    trimBottom.position.set(0, -0.545, 0.001);
    trimBottom.renderOrder = 101;
    this.group.add(trimBottom);

    // status text panel (top)
    this.statusCanvas = document.createElement('canvas');
    this.statusCanvas.width = 512;
    this.statusCanvas.height = 176;
    this.statusCtx = this.statusCanvas.getContext('2d')!;
    this.statusTexture = new THREE.CanvasTexture(this.statusCanvas);
    this.statusTexture.magFilter = THREE.LinearFilter;
    const status = new THREE.Mesh(
      new THREE.PlaneGeometry(1.05, 0.36),
      this.uiMaterial({ map: this.statusTexture, transparent: true })
    );
    status.position.set(0, 0.35, 0.002);
    status.renderOrder = 102;
    this.group.add(status);
    this.logoImage = new Image();
    this.logoImage.onload = () => this.paintStatus();
    this.logoImage.src = '/brand/glitch-pursuit-logo.svg';
    this.setStatus(['GLITCH PURSUIT']);
    // display fonts load async — repaint everything once they're in
    document.fonts?.ready?.then(() => {
      this.paintStatus();
      for (const b of this.buttons) b.paintLabel();
      this.codeCells.forEach((_, i) => this.paintCodeCell(i));
    });

    // exit VR — top-right corner, available in every mode
    this.addButton('EXIT VR', 0x3a3a44, ['main', 'lobby', 'solo', 'briefing', 'debrief'], { x: 0.43, y: -0.495, w: 0.22, h: 0.055 }, actions.exit, 'exit');

    // --- main menu buttons ---
    this.addButton('SOLO RUN', 0xff2d95, ['main'], { x: 0, y: 0.12, w: 0.62 }, actions.solo, 'connect-solo');
    this.addButton('JOIN RANDOM', 0x14b8a8, ['main'], { x: 0, y: -0.02, w: 0.62 }, actions.random, 'connect-random');
    this.addButton('CREATE LOBBY', 0x5f3dc4, ['main'], { x: 0, y: -0.16, w: 0.62 }, actions.create, 'connect-create');

    // --- join-by-code row: 5 cycling character cells + GO ---
    const cellXs = [-0.42, -0.30, -0.18, -0.06, 0.06];
    cellXs.forEach((x, i) => {
      this.addCodeCell(i, x, -0.36);
      this.addButton('^', 0x23262e, ['main'], { x, y: -0.265, w: 0.1, h: 0.07 }, () => this.cycleCode(i, 1));
      this.addButton('v', 0x23262e, ['main'], { x, y: -0.455, w: 0.1, h: 0.07 }, () => this.cycleCode(i, -1));
    });
    this.addButton('JOIN >', 0x2ee6d6, ['main'], { x: 0.33, y: -0.36, w: 0.38 },
      () => actions.joinCode(this.codeChars.join('')), 'connect-code');

    // --- lobby buttons ---
    this.addButton('DRIVE', 0x23262e, ['lobby'], { x: -0.36, y: -0.05, w: 0.3 }, actions.drive, 'seat-drive');
    this.addButton('GUN', 0x23262e, ['lobby'], { x: 0, y: -0.05, w: 0.3 }, actions.gun, 'seat-gun');
    this.addButton('RANDOM', 0x23262e, ['lobby'], { x: 0.36, y: -0.05, w: 0.3 }, actions.seatRandom, 'seat-random');
    this.addButton('READY', 0xff2d95, ['lobby', 'solo'], { x: 0, y: -0.26, w: 0.62 }, actions.ready, 'ready');
    this.addButton('SKIP BRIEFING', 0x14b8a8, ['briefing'], { x: 0, y: -0.26, w: 0.62 }, actions.skip, 'skip');
    this.addButton('CONTINUE', 0xff2d95, ['debrief'], { x: 0, y: -0.26, w: 0.62 }, actions.ready, 'continue');
  }

  /** All menu materials draw over the world so the car can't hide the UI. */
  private uiMaterial(params: THREE.MeshBasicMaterialParameters): THREE.MeshBasicMaterial {
    const mat = new THREE.MeshBasicMaterial({ ...params, side: THREE.DoubleSide, toneMapped: false });
    mat.depthTest = false;
    mat.depthWrite = false;
    return mat;
  }

  private addButton(
    label: string,
    color: number,
    modes: VRMenuMode[],
    layout: { x: number; y: number; w: number; h?: number },
    action: () => void,
    id?: string
  ): void {
    const height = layout.h ?? 0.11;
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 48;
    const ctx = canvas.getContext('2d')!;
    const texture = new THREE.CanvasTexture(canvas);
    texture.magFilter = THREE.NearestFilter;
    const paintLabel = () => {
      ctx.clearRect(0, 0, 256, 48);
      ctx.fillStyle = '#ffffff';
      ctx.font = "26px 'Audiowide', 'Courier New', monospace";
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, 128, 26);
      texture.needsUpdate = true;
    };
    paintLabel();

    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(layout.w, height),
      this.uiMaterial({ color })
    );
    mesh.position.set(layout.x, layout.y, 0.004);
    mesh.renderOrder = 101;

    const text = new THREE.Mesh(
      new THREE.PlaneGeometry(layout.w * 0.9, Math.min(height * 0.8, 0.085)),
      this.uiMaterial({ map: texture, transparent: true })
    );
    text.position.z = 0.002;
    text.renderOrder = 102;
    mesh.add(text);

    this.group.add(mesh);
    this.buttons.push({ mesh, action, baseColor: color, modes, id, paintLabel });
  }

  /** One character slot of the join code (display only; arrows cycle it). */
  private addCodeCell(index: number, x: number, y: number): void {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    const texture = new THREE.CanvasTexture(canvas);
    texture.magFilter = THREE.NearestFilter;
    this.codeCells[index] = { ctx, texture };

    const cell = new THREE.Mesh(
      new THREE.PlaneGeometry(0.1, 0.11),
      this.uiMaterial({ color: 0x10131c })
    );
    cell.position.set(x, y, 0.004);
    cell.renderOrder = 101;
    const glyph = new THREE.Mesh(
      new THREE.PlaneGeometry(0.085, 0.095),
      this.uiMaterial({ map: texture, transparent: true })
    );
    glyph.position.z = 0.002;
    glyph.renderOrder = 102;
    cell.add(glyph);
    this.group.add(cell);
    // cells are visible in 'main' mode only — reuse the button visibility pass
    this.buttons.push({ mesh: cell, action: () => this.cycleCode(index, 1), baseColor: 0x10131c, modes: ['main'], paintLabel: () => this.paintCodeCell(index) });

    this.paintCodeCell(index);
  }

  private paintCodeCell(index: number): void {
    const cell = this.codeCells[index];
    if (!cell) return;
    cell.ctx.clearRect(0, 0, 64, 64);
    cell.ctx.fillStyle = '#2ee6d6';
    cell.ctx.font = "44px 'Audiowide', 'Courier New', monospace";
    cell.ctx.textAlign = 'center';
    cell.ctx.textBaseline = 'middle';
    cell.ctx.fillText(this.codeChars[index], 32, 34);
    cell.texture.needsUpdate = true;
  }

  private cycleCode(index: number, step: number): void {
    const at = CODE_ALPHABET.indexOf(this.codeChars[index]);
    const next = (at + step + CODE_ALPHABET.length) % CODE_ALPHABET.length;
    this.codeChars[index] = CODE_ALPHABET[next];
    this.paintCodeCell(index);
  }

  /** Hook up controller rays + trigger events (idempotent). */
  attachControllers(controllers: THREE.XRTargetRaySpace[]): void {
    for (const controller of controllers) {
      if (this.controllers.includes(controller)) continue;
      this.controllers.push(controller);

      // aiming laser (only rendered while the menu is visible)
      const laserGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(0, 0, -3),
      ]);
      const laserMat = new THREE.LineBasicMaterial({ color: 0x2ee6d6, transparent: true, opacity: 0.6 });
      laserMat.depthTest = false;
      const laser = new THREE.Line(laserGeo, laserMat);
      laser.renderOrder = 100;
      laser.visible = false;
      controller.add(laser);
      this.lasers.set(controller, laser);

      controller.addEventListener('selectstart', () => {
        if (this.mode === 'hidden') return;
        const hit = this.intersect(controller);
        if (!hit || this.isDisabled(hit)) return;
        const now = performance.now();
        if (now - this.lastActivationAt < 280) return;
        this.lastActivationAt = now;
        hit.mesh.scale.setScalar(0.94);
        window.setTimeout(() => {
          hit.mesh.scale.setScalar(hit === this.hovered ? 1.08 : 1);
        }, 110);
        hit.action();
      });
    }
  }

  setBusy(busy: boolean): void {
    this.busy = busy;
    for (const button of this.buttons) {
      if (!button.id?.startsWith('connect-')) continue;
      const material = button.mesh.material as THREE.MeshBasicMaterial;
      material.transparent = true;
      material.opacity = busy ? 0.38 : 1;
      button.mesh.scale.setScalar(1);
    }
  }

  setMode(mode: VRMenuMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.group.visible = mode !== 'hidden';
    for (const button of this.buttons) {
      button.mesh.visible = button.modes.includes(mode);
    }
    for (const laser of this.lasers.values()) {
      laser.visible = mode !== 'hidden';
    }
  }

  /** Up to 4 lines; line 0 renders as the neon title. */
  setStatus(lines: string[]): void {
    this.statusLines = lines;
    this.paintStatus();
  }

  private paintStatus(): void {
    const ctx = this.statusCtx;
    ctx.clearRect(0, 0, 512, 176);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    this.statusLines.slice(0, 4).forEach((line, i) => {
      if (i === 0) {
        if (line === 'GLITCH PURSUIT' && this.logoImage?.complete) {
          ctx.drawImage(this.logoImage, 56, 0, 400, 135);
        } else {
          let titleSize = 42;
          do {
            ctx.font = `italic 700 ${titleSize}px 'Audiowide', 'Courier New', monospace`;
            titleSize -= 2;
          } while (ctx.measureText(line).width > 460 && titleSize >= 26);
          ctx.fillStyle = '#20eaf0';
          ctx.fillText(line, 260, 53);
          ctx.shadowColor = '#ff168c';
          ctx.shadowBlur = 14;
          ctx.fillStyle = '#ff2d95';
          ctx.fillText(line, 256, 48);
          ctx.shadowBlur = 0;
        }
      } else {
        ctx.fillStyle = i === 1 ? '#ffffff' : '#2ee6d6';
        ctx.font = i === 1 ? "22px 'Courier New', monospace" : "24px 'Audiowide', 'Courier New', monospace";
        ctx.fillText(line, 256, 52 + i * 38);
      }
    });
    this.statusTexture.needsUpdate = true;
  }

  /** Highlight the chosen seat button (and READY state) like the desktop UI. */
  setSelected(id: string | null, ready: boolean): void {
    this.selectedId = id;
    for (const button of this.buttons) {
      if (!button.id) continue;
      const mat = button.mesh.material as THREE.MeshBasicMaterial;
      if (button.id === 'ready') {
        mat.color.setHex(ready ? 0x2f9e44 : button.baseColor);
      } else if (button !== this.hovered) {
        mat.color.setHex(this.effectiveColor(button));
      }
    }
  }

  private effectiveColor(button: MenuButton): number {
    if (button.id && button.id === this.selectedId) return 0xff2d95;
    return button.baseColor;
  }

  /** Per-frame: hover highlight + laser length. */
  update(): void {
    if (this.mode === 'hidden') return;

    let closest: MenuButton | null = null;
    for (const controller of this.controllers) {
      const hit = this.intersect(controller);
      if (hit && !this.isDisabled(hit)) closest = hit;
    }

    if (closest !== this.hovered) {
      if (this.hovered) {
        (this.hovered.mesh.material as THREE.MeshBasicMaterial).color.setHex(this.effectiveColor(this.hovered));
        this.hovered.mesh.scale.setScalar(1);
      }
      this.hovered = closest;
      if (closest) {
        const mat = closest.mesh.material as THREE.MeshBasicMaterial;
        mat.color.setHex(brighten(this.effectiveColor(closest)));
        closest.mesh.scale.setScalar(1.08);
      }
    }
  }

  private intersect(controller: THREE.XRTargetRaySpace): MenuButton | null {
    const origin = controller.getWorldPosition(new THREE.Vector3());
    const dir = new THREE.Vector3(0, 0, -1)
      .applyQuaternion(controller.getWorldQuaternion(new THREE.Quaternion()));
    this.raycaster.set(origin, dir);
    this.raycaster.far = 6;

    const visible = this.buttons.filter((b) => b.mesh.visible);
    const hits = this.raycaster.intersectObjects(visible.map((b) => b.mesh), false);
    if (hits.length === 0) return null;
    return visible.find((b) => b.mesh === hits[0].object) ?? null;
  }

  private isDisabled(button: MenuButton): boolean {
    return this.busy && Boolean(button.id?.startsWith('connect-'));
  }
}

function brighten(color: number): number {
  const c = new THREE.Color(color);
  c.lerp(new THREE.Color(0xffffff), 0.35);
  return c.getHex();
}
